import React, { useState, useRef } from 'react';
import { X, Save, Upload } from 'lucide-react';
import { UPDATE_FOOD_URL } from '../../config/api';
import { FOOD_FALLBACK_IMG, getAllImageUrls } from '../Utils/apiHelpers';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { trackFood, trackError } from '../Utils/analytics';
import { useImagePreviews } from '../Utils/useImagePreviews';
import UnsavedChangesConfirm from '../UnsavedChangesConfirm/UnsavedChangesConfirm';
import './EditFoodModal.css';

interface FoodListing {
  food_id: number;
  food_name: string;
  description?: string;
  price: number;
  category?: string;
  phone_number?: string;
  pickup_location?: string;
  serving_size?: string;
  preparation_time?: string;
  available_quantity: number;
  image_urls?: string | string[];
}

interface EditFoodModalProps {
  food: FoodListing;
  onClose: () => void;
  onSuccess: () => void;
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const EditFoodModal: React.FC<EditFoodModalProps> = ({ food, onClose, onSuccess, showNotification }) => {
  const existingImages: string[] = getAllImageUrls(food.image_urls);

  const [form, setForm] = useState({
    food_name: food.food_name || '',
    description: food.description || '',
    price: String(food.price ?? ''),
    category: food.category || '',
    phone_number: food.phone_number || '',
    pickup_location: food.pickup_location || '',
    serving_size: food.serving_size || '',
    preparation_time: food.preparation_time || '',
    available_quantity: String(food.available_quantity ?? ''),
  });

  const { previews, addFiles, removeAt, clearAll } = useImagePreviews();
  const [removedImages, setRemovedImages] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialForm = {
    food_name: food.food_name || '',
    description: food.description || '',
    price: String(food.price ?? ''),
    category: food.category || '',
    phone_number: food.phone_number || '',
    pickup_location: food.pickup_location || '',
    serving_size: food.serving_size || '',
    preparation_time: food.preparation_time || '',
    available_quantity: String(food.available_quantity ?? ''),
  };

  const hasUnsavedChanges =
    form.food_name !== initialForm.food_name ||
    form.description !== initialForm.description ||
    form.price !== initialForm.price ||
    form.category !== initialForm.category ||
    form.phone_number !== initialForm.phone_number ||
    form.pickup_location !== initialForm.pickup_location ||
    form.serving_size !== initialForm.serving_size ||
    form.preparation_time !== initialForm.preparation_time ||
    form.available_quantity !== initialForm.available_quantity ||
    previews.length > 0 ||
    removedImages.size > 0;

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      clearAll();
      onClose();
    }
  };

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validFiles = files.filter(file => {
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, images: 'Only JPG, PNG, and WebP images are allowed' }));
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, images: 'Each image must be less than 5MB' }));
        return false;
      }
      return true;
    });

    const keptExisting = existingImages.filter(url => !removedImages.has(url));
    const totalImages = keptExisting.length + previews.length + validFiles.length;
    if (totalImages > 5) {
      setErrors(prev => ({ ...prev, images: 'Maximum 5 images allowed' }));
      e.target.value = '';
      return;
    }

    if (validFiles.length > 0) {
      addFiles(validFiles);
      if (errors.images) setErrors(prev => { const n = { ...prev }; delete n.images; return n; });
    }
    e.target.value = '';
  };

  const removeNewImage = (index: number) => {
    removeAt(index);
  };

  const removeExistingImage = (url: string) => {
    setRemovedImages(prev => new Set(prev).add(url));
  };

  const restoreExistingImage = (url: string) => {
    setRemovedImages(prev => {
      const next = new Set(prev);
      next.delete(url);
      return next;
    });
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.food_name.trim()) e.food_name = 'Required';
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) <= 0) e.price = 'Must be a positive number';
    if (!form.category) e.category = 'Required';
    if (form.phone_number && !/^[+]?[0-9\s\-()]{7,}$/.test(form.phone_number.replace(/\s/g, ''))) e.phone_number = 'Enter a valid UK phone number';
    if (form.available_quantity && (isNaN(Number(form.available_quantity)) || Number(form.available_quantity) < 0 || !Number.isInteger(Number(form.available_quantity)))) {
      e.available_quantity = 'Must be a non-negative whole number';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const remainingExisting = existingImages.filter(url => !removedImages.has(url));
    const totalImages = remainingExisting.length + previews.length;
    if (totalImages === 0) {
      setErrors({ images: 'At least one image is required' });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('food_name', form.food_name.trim());
      formData.append('description', form.description.trim());
      formData.append('price', form.price);
      formData.append('category', form.category);
      formData.append('phone_number', form.phone_number.replace(/\s/g, ''));
      formData.append('pickup_location', form.pickup_location.trim());
      formData.append('serving_size', form.serving_size.trim());
      formData.append('preparation_time', form.preparation_time.trim());
      formData.append('available_quantity', form.available_quantity);

      // Send existing image URLs separately so the backend preserves them
      remainingExisting.forEach(url => formData.append('existing_images', url));
      // Send new File objects for upload
      previews.forEach(item => formData.append('images', item.file));

      const res = await authenticatedFetch(UPDATE_FOOD_URL(food.food_id), {
        method: 'PUT',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        showNotification('success', 'Food Updated', `${food.food_name} has been updated successfully.`);
        trackFood('food_updated', food.food_name);
        clearAll();
        onSuccess();
        onClose();
      } else {
        showNotification('error', 'Update Failed', data.message || 'Failed to update food item.');
        trackError('food_update_failed', 'edit_food_modal');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
      trackError('food_update_network_error', 'edit_food_modal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const field = (id: string, label: string, type: string = 'text', extra?: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div className="efm-group">
      <label htmlFor={id} className="efm-label">{label}</label>
      <input
        id={id}
        type={type}
        className={`efm-input${errors[id] ? ' efm-input--err' : ''}`}
        value={(form as any)[id]}
        onChange={e => handleChange(id, e.target.value)}
        {...extra}
      />
      {errors[id] && <span className="efm-error">{errors[id]}</span>}
    </div>
  );

  const keptExisting = existingImages.filter(url => !removedImages.has(url));

  return (
    <div className="efm-overlay" onClick={requestClose}>
      <div className="efm-modal" onClick={e => e.stopPropagation()}>
        <button className="efm-close" onClick={requestClose}><X size={20} /></button>

        <h3 className="efm-title">Edit Food Item</h3>
        <p className="efm-subtitle">Update the details below and save your changes.</p>

        <form onSubmit={handleSubmit} className="efm-form">
          <div className="efm-row">
            {field('food_name', 'Food Name *', 'text', { maxLength: 300 })}
            {field('price', 'Price (£) *', 'number', { min: '0', step: '0.01' })}
          </div>

          <div className="efm-group">
            <label htmlFor="efm-desc" className="efm-label">Description</label>
            <textarea
              id="efm-desc"
              className="efm-textarea"
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="efm-row">
            <div className="efm-group">
              <label htmlFor="efm-cat" className="efm-label">Category *</label>
              <select
                id="efm-cat"
                className={`efm-input${errors.category ? ' efm-input--err' : ''}`}
                value={form.category}
                onChange={e => handleChange('category', e.target.value)}
              >
                <option value="">Select category</option>
                <option value="veg">Vegetarian</option>
                <option value="non-veg">Non-Vegetarian</option>
              </select>
              {errors.category && <span className="efm-error">{errors.category}</span>}
            </div>
            {field('phone_number', 'Phone Number', 'tel', { maxLength: 20, placeholder: 'e.g., 07123 456789 or +44 7123 456789' })}
          </div>

          <div className="efm-row">
            {field('serving_size', 'Serving Size')}
            {field('preparation_time', 'Preparation Time')}
          </div>

          {field('pickup_location', 'Pickup Location')}
          {field('available_quantity', 'Available Quantity', 'number', { min: '0', step: '1' })}

          {/* Image management */}
          <div className="efm-group">
            <label className="efm-label">Images</label>
            <p className="efm-hint">Keep existing images or upload new ones (max 5 total, JPG/PNG/WebP, 5MB each).</p>

            {/* Existing images */}
            {existingImages.length > 0 && (
              <div className="efm-img-grid">
                {existingImages.map(url => {
                  const removed = removedImages.has(url);
                  return (
                    <div key={url} className={`efm-img-preview${removed ? ' efm-img-preview--removed' : ''}`}>
                      <img src={url} alt="Existing" className="efm-img-thumb" onError={e => { (e.target as HTMLImageElement).src = FOOD_FALLBACK_IMG; }} />
                      {removed ? (
                        <button type="button" className="efm-img-restore" onClick={() => restoreExistingImage(url)}>Restore</button>
                      ) : (
                        <button type="button" className="efm-img-remove" onClick={() => removeExistingImage(url)}><X size={14} /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* New image previews */}
            {previews.length > 0 && (
              <div className="efm-img-grid">
                {previews.map((item, index) => (
                  <div key={item.url} className="efm-img-preview efm-img-preview--new">
                    <img src={item.url} alt={`New ${index + 1}`} className="efm-img-thumb" />
                    <button type="button" className="efm-img-remove" onClick={() => removeNewImage(index)}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload button */}
            {keptExisting.length + previews.length < 5 && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className="efm-file-hidden"
                />
                <label className="efm-upload-btn" onClick={() => fileInputRef.current?.click()}>
                  <Upload size={16} />
                  Upload Images
                </label>
              </>
            )}

            <span className="efm-hint">{keptExisting.length + previews.length}/5 images</span>
            {errors.images && <span className="efm-error">{errors.images}</span>}
          </div>

          {errors.general && <p className="efm-error efm-error--general">{errors.general}</p>}

          <div className="efm-actions">
            <button type="button" className="efm-btn efm-btn--cancel" onClick={requestClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="efm-btn efm-btn--save" disabled={isSubmitting}>
              {isSubmitting ? <><span className="efm-spinner" />Saving...</> : <><Save size={16} />Save Changes</>}
            </button>
          </div>
        </form>
      </div>
      {showDiscardConfirm && (
        <UnsavedChangesConfirm
          onConfirm={() => { setShowDiscardConfirm(false); clearAll(); onClose(); }}
          onCancel={() => setShowDiscardConfirm(false)}
        />
      )}
    </div>
  );
};

export default EditFoodModal;
